import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AppLayout from "../../components/layout/AppLayout";
import { salesService } from "../../services/salesService";
import { useNotification } from "../../context/NotificationContext";
import { useAuth } from "../../context/AuthContext";

const SalesReceipt = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();
  const receiptId = searchParams.get("id"); // Mengambil ID transaksi dari URL

  // --- MEMORI (STATE): Menyimpan data detail Nota ---
  const [saleData, setSaleData] = useState(null); // Data ringkasan (Total, Tanggal, Pembeli)
  const [saleItems, setSaleItems] = useState([]); // Data rincian (Barang apa saja yang dibeli)
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!receiptId) {
      navigate("/sales-history");
      return;
    }

    /**
     * --- LOGIKA: Mengambil Data Nota Lengkap ---
     * Mengambil data dari tabel 'sales' dan 'sale_items' sekaligus.
     */
    const fetchReceiptData = async () => {
      try {
        setLoading(true);

        const { sale, items } = await salesService.getReceiptData(receiptId);
        setSaleData(sale);
        setSaleItems(items);
      } catch (error) {
        console.error("Error fetching receipt:", error);
        showNotification({
          type: "error",
          title: "Data Tidak Ditemukan",
          message: "Gagal memuat detail transaksi. Mungkin data telah dihapus.",
        });
        navigate("/sales-history");
      } finally {
        setLoading(false);
      }
    };

    fetchReceiptData();
  }, [receiptId, navigate, showNotification]);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
    }).format(val || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <AppLayout pageTitle="Detail Struk">
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm font-medium text-on-surface-variant animate-pulse">
              Memuat Struk Transaksi...
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!saleData) return null;

  return (
    <AppLayout pageTitle={`Struk #${saleData.transaction_id}`}>
      <div className="space-y-6 max-w-4xl mx-auto print:p-0 print:m-0 print:bg-white print:text-black">
        {/* Navigasi (Hilang saat dicetak) */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 print:hidden">
          <nav className="flex items-center gap-2 text-[10px] font-label uppercase tracking-widest text-on-surface-variant">
            <button
              onClick={() => navigate("/sales-history")}
              className="hover:text-primary transition-colors hover:underline"
            >
              Riwayat
            </button>
            <span className="material-symbols-outlined text-[14px]">
              chevron_right
            </span>
            <span className="font-bold text-on-surface">Detail Struk</span>
          </nav>
        </div>

        {/* AREA NOTA / STRUK */}
        <div className="bg-surface-container-lowest rounded-3xl p-6 md:p-10 shadow-sm ring-1 ring-outline-variant/30 ambient-bloom relative overflow-hidden print:shadow-none print:ring-0 print:rounded-none print:p-0 print:bg-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 z-0 print:hidden pointer-events-none"></div>

          <div className="relative z-10 space-y-8">
            {/* Header Nota */}
            <div className="flex flex-col items-center text-center space-y-2 border-b border-outline-variant/20 pb-8 print:border-black/20">
              <div className="w-16 h-16 bg-surface-container-highest rounded-2xl flex items-center justify-center mb-2 print:border print:border-black print:bg-white">
                <span className="material-symbols-outlined text-3xl text-primary print:text-black">
                  receipt_long
                </span>
              </div>
              <h1 className="font-headline text-2xl md:text-3xl font-extrabold text-on-surface tracking-tight print:text-black">
                {user?.user_metadata?.store_name || "Harapan Kita"}
              </h1>
              <p className="text-sm font-medium text-on-surface-variant print:text-gray-700">
                Resi Penjualan
              </p>
            </div>

            {/* Informasi Transaksi (ID, Tanggal, Pelanggan, Kasir) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 bg-surface-container-low/50 p-6 rounded-2xl border border-outline-variant/20 print:bg-white print:border-black/20 print:rounded-lg">
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-outline uppercase tracking-widest print:text-gray-500">
                  ID Transaksi
                </p>
                <p className="text-sm font-bold text-on-surface font-mono print:text-black">
                  {saleData.transaction_id}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-outline uppercase tracking-widest print:text-gray-500">
                  Tanggal Transaksi
                </p>
                <p className="text-sm font-bold text-on-surface print:text-black">
                  {formatDate(saleData.created_at)}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-outline uppercase tracking-widest print:text-gray-500">
                  Nama Pelanggan
                </p>
                <p className="text-sm font-bold text-on-surface print:text-black">
                  {saleData.customer_name || "Pelanggan Umum"}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-outline uppercase tracking-widest print:text-gray-500">
                  Kasir / Penerbit
                </p>
                <p className="text-sm font-bold text-on-surface print:text-black">
                  {saleData.profiles?.full_name || "Tim Penjualan"}
                </p>
              </div>
            </div>

            {/* TABEL RINCIAN BARANG */}
            <div className="overflow-x-auto rounded-2xl border border-outline-variant/20 print:border-black/20 print:rounded-lg hidden md:block">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-surface-container-low print:bg-gray-100">
                    <th className="px-6 py-4 text-[11px] font-bold text-outline uppercase tracking-widest print:text-gray-600">
                      Item Produk
                    </th>
                    <th className="px-6 py-4 text-[11px] font-bold text-outline uppercase tracking-widest print:text-gray-600 text-center">
                      SKU
                    </th>
                    <th className="px-6 py-4 text-[11px] font-bold text-outline uppercase tracking-widest print:text-gray-600 text-right">
                      Kuantitas
                    </th>
                    <th className="px-6 py-4 text-[11px] font-bold text-outline uppercase tracking-widest print:text-gray-600 text-right">
                      Harga Satuan
                    </th>
                    <th className="px-6 py-4 text-[11px] font-bold text-outline uppercase tracking-widest print:text-gray-600 text-right">
                      Subtotal
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-outline-variant/10 print:divide-black/10">
                  {saleItems.length === 0 ? (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-6 py-8 text-center text-sm text-on-surface-variant italic"
                      >
                        Tidak ada barang ditemukan.
                      </td>
                    </tr>
                  ) : (
                    saleItems.map((item, index) => {
                      /**
                       * LOGIKA: Menangani Barang yang Sudah Dihapus/Ganti Nama
                       * Jika barang di master data sudah tidak ada, tetap tampilkan 
                       * nama barang yang tersimpan di nota saat transaksi terjadi.
                       */
                      const currentName = item.products?.name;
                      const pastName = item.product_name;
                      const isNameChanged = currentName && pastName && currentName !== pastName;
                      const mainName = currentName || pastName || "Produk Dihapus";

                      const currentSku = item.products?.sku;
                      const pastSku = item.product_sku;
                      const mainSku = currentSku || pastSku || "N/A";

                      return (
                        <tr
                          key={index}
                          className="hover:bg-surface-container-low/30 transition-colors print:hover:bg-transparent"
                        >
                          <td className="px-6 py-4">
                            <p className="font-bold text-sm text-on-surface print:text-black">
                              {mainName}
                            </p>
                            {isNameChanged && (
                              <p className="text-[10px] text-on-surface-variant font-medium mt-1 inline-flex bg-surface-container px-1.5 py-0.5 rounded border border-outline-variant/10">
                                Saat Dijual: {pastName}
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4 text-center">
                            <span className="text-xs font-mono text-outline-variant bg-surface-container-highest px-2 py-1 rounded-md print:bg-transparent print:border print:border-gray-300 print:text-black">
                              {mainSku}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm font-bold text-on-surface text-right print:text-black">
                            x{item.quantity}
                          </td>
                          <td className="px-6 py-4 text-sm text-on-surface-variant font-medium text-right print:text-gray-700">
                            {formatCurrency(item.unit_price)}
                          </td>
                          <td className="px-6 py-4 text-sm font-extrabold text-primary text-right print:text-black">
                            {formatCurrency(item.subtotal)}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Versi Mobile Kartu (Sembunyikan saat dicetak) */}
            <div className="grid grid-cols-1 gap-4 md:hidden print:hidden">
              {saleItems.length === 0 ? (
                <div className="text-center py-8 text-sm text-on-surface-variant italic border border-outline-variant/20 rounded-xl"> Tidak ada barang ditemukan. </div>
              ) : (
                saleItems.map((item, index) => {
                  const currentName = item.products?.name;
                  const pastName = item.product_name;
                  const isNameChanged = currentName && pastName && currentName !== pastName;
                  const mainName = currentName || pastName || "Produk Dihapus";

                  const currentSku = item.products?.sku;
                  const pastSku = item.product_sku;
                  const mainSku = currentSku || pastSku || "N/A";

                  return (
                    <div key={index} className="bg-surface-container-lowest rounded-xl p-4 border border-outline-variant/20 flex flex-col gap-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold text-sm text-on-surface">{mainName}</p>
                          {isNameChanged && (
                            <p className="text-[10px] text-on-surface-variant font-medium mt-0.5">
                              (Saat dijual: {pastName})
                            </p>
                          )}
                          <span className="text-[10px] font-mono text-outline-variant bg-surface-container-highest px-2 py-0.5 rounded mt-1 inline-block">{mainSku}</span>
                        </div>
                        <span className="text-sm font-bold text-on-surface">x{item.quantity}</span>
                      </div>
                      <div className="flex justify-between items-center mt-2 pt-3 border-t border-outline-variant/10">
                        <span className="text-xs text-on-surface-variant font-medium">{formatCurrency(item.unit_price)} / unit</span>
                        <span className="text-sm font-extrabold text-primary">{formatCurrency(item.subtotal)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* RINGKASAN TOTAL PEMBAYARAN */}
            <div className="flex justify-end pt-4">
              <div className="w-full md:w-1/2 lg:w-1/3 bg-surface-container-highest rounded-2xl p-6 space-y-4 print:bg-transparent print:border print:border-black/20 print:rounded-lg">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-on-surface-variant font-semibold print:text-gray-600">
                    Subtotal:
                  </span>
                  <span className="font-bold text-on-surface print:text-black">
                    {formatCurrency(saleData.subtotal)}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-on-surface-variant font-semibold print:text-gray-600">
                    Pajak (0%):
                  </span>
                  <span className="font-bold text-on-surface print:text-black">
                    {formatCurrency(saleData.tax)}
                  </span>
                </div>
                <div className="pt-4 border-t border-outline-variant/30 flex justify-between items-center print:border-black/20">
                  <span className="font-headline font-black text-lg text-on-surface print:text-black">
                    Total:
                  </span>
                  <span className="font-headline font-black text-xl text-primary print:text-black">
                    {formatCurrency(saleData.total)}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer Struk (Hanya Pas Dicetak) */}
            <div className="hidden print:block pt-16 text-center text-sm text-gray-500 font-medium">
              <p>Terima kasih atas pembelian Anda.</p>
              <p className="mt-1 text-xs">
                Dicetak pada: {new Date().toLocaleString("id-ID")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default SalesReceipt;
